import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { defineStore } from 'pinia'

import { ContactClass } from '@types'

import {
  createBunchElement,
  duplicateBunchElement,
  insertBunchElementAt,
  isDirtyList,
  unsavedGridBlocker,
  unshiftBunchElement,
} from '../utils/helpers'

import { ROUTE_NAME } from '@/const'

import { useMainStore } from '@/store/main'
import { useModalsStore } from '../modals'
import { useContactsStore } from './'
import { useRepositoriesStore } from '../repositories/index'

export const useContactsBunchStore = defineStore('contacts-bunch', () => {
  // INIT

  const mainStore = useMainStore()
  const modalsStore = useModalsStore()
  const contactsStore = useContactsStore()
  const repositoryStore = useRepositoriesStore()

  const router = useRouter()

  const contactsList = computed(() => contactsStore.getList)

  // STORE

  const initFlag = ref(false)
  const list = ref<Map<string, ContactClass>>(new Map())

  // ACTIONS

  const buildElement = () => createBunchElement(ContactClass, {
    repository_id: repositoryStore.getCurrentId,
  })

  const createElement = () => {
    const element = buildElement()
    return insertBunchElementAt(element, list)
  }

  const createElementAt = (index: number) => {
    const element = buildElement()
    return insertBunchElementAt(element, list, index)
  }

  const unshiftElement = (element: ContactClass) => unshiftBunchElement(element, list)

  const duplicateElement = (id: string) => duplicateBunchElement(id, list, ContactClass)

  // GETTERS

  const getList = computed(() => (list.value.size ? list.value : new Map()))

  const getElementById = (id: string) => list.value.get(id) as ContactClass

  const isDirty = computed(() => isDirtyList(list.value))

  // FILL STORE

  watch(contactsList, value => {
    value.forEach(item => {
      if (!list.value.get(item.id)) {
        const instance = new ContactClass(item)
        list.value.set(item.id, instance)
        instance.validateData()
      }
    })
    initFlag.value = true
  })

  // ADD BLOCKER IF DIRTY

  watch(isDirty, value => {
    if (value) {
      mainStore.addBlocker(
        `${ROUTE_NAME.CONTACTS}-hasChanges`,
        unsavedGridBlocker('contacts', () => {
          router.push({ name: ROUTE_NAME.CONTACTS })
        },
        () => {
          modalsStore.closeModals(ContactClass)
          getList.value.forEach(item => {
            item.cancel()
          })
        }
        ),
      )
    } else {
      mainStore.removeBlocker(`${ROUTE_NAME.CONTACTS}-hasChanges`)
    }
  })

  const validateAll = () => {
    list.value.forEach(item => {
      item.validateData()
    })
  }

  // CLEAR STORE

  const clear = () => {
    initFlag.value = false
    list.value = new Map()
  }

  return {
    initFlag,

    createElement,
    createElementAt,
    unshiftElement,
    duplicateElement,

    getList,
    getElementById,
    isDirty,

    validateAll,

    clear,
  }
})
